import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import { userConnectionWrapper } from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.actions'
import { creditCardOption } from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { BookkeeperAccountVisibilityTypes } from 'src/enums'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { UserDepartment } from 'src/models/user.models'
import {
  PeopleInvitedToOrgDetailMatchParams,
  peopleInvitedToOrgDetailRouteProps,
  PeopleMembersDetailMatchParams,
  peopleMembersDetailRouteProps,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { CreditCardsFullPaginatedDataState } from 'src/redux/creditCardsFullPaginatedData.reducer'
import { selectContactsListModelsFromState } from 'src/redux/selectors/data/contacts.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectMembersListModelsFromState } from 'src/redux/selectors/data/members.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { ErrorDetailMap } from 'src/utils/error.utils'

export const selectFetchSuccessFromState = (state: RootState): boolean =>
  state.app.peopleDetail.fetchSuccess
export const selectFetchFailureFromState = (state: RootState): boolean =>
  state.app.peopleDetail.fetchFailure
export const selectLoadingFromState = (state: RootState): boolean =>
  state.app.peopleDetail.loading

export const selectCreditCardsLoadingFromState = (state: RootState): boolean =>
  state.app.peopleDetail.creditCardsLoading

export const selectLoadingPersonOrgRole = (state: RootState): boolean =>
  state.app.peopleDetail.loadingPersonOrgRole
export const selectUpdatePersonOrgRoleTopLevelError = (
  state: RootState,
): string | undefined => state.app.peopleDetail.updatePersonOrgRoleTopLevelError

export const selectMemberIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<PeopleMembersDetailMatchParams>(
      location.pathname,
      peopleMembersDetailRouteProps,
    ) as match<PeopleMembersDetailMatchParams>
    return mtch?.params.id
  },
)

export const selectInviteUserIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<PeopleInvitedToOrgDetailMatchParams>(
      location.pathname,
      peopleInvitedToOrgDetailRouteProps,
    ) as match<PeopleInvitedToOrgDetailMatchParams>
    return mtch?.params.id
  },
)

export const selectPerson = createSelector(
  [
    selectMembersListModelsFromState,
    selectContactsListModelsFromState,
    selectUserListModelsFromState,
    selectMemberIdFromRoute,
    selectInviteUserIdFromRoute,
  ],
  (members, contacts, users, memberUserId, inviteUserId) => {
    if (contacts[memberUserId || inviteUserId] !== undefined) {
      return contacts[memberUserId || inviteUserId]
    } else if (members[memberUserId] !== undefined) {
      return userConnectionWrapper(members[memberUserId])
    } else if (users[inviteUserId] !== undefined) {
      return userConnectionWrapper(users[inviteUserId])
    }
    return undefined
  },
)

export const selectPersonFromMembers = createSelector(
  [selectMembersListModelsFromState, selectMemberIdFromRoute],
  (members, userId) =>
    members[userId] !== undefined ? members[userId] : undefined,
)

export const selectPatchingPeopleDetailInfoForm = (state: RootState): boolean =>
  state.app.peopleDetail.patchingPeopleDetailInfoForm

export const selectPatchingPeopleDetailInfoErrorDetailMap = (
  state: RootState,
): ErrorDetailMap<string> =>
  state.app.peopleDetail.patchingPeopleDetailInfoErrorDetailMap ?? {}
export const selectCreditCardsFullPaginatedData = (
  state: RootState,
): CreditCardsFullPaginatedDataState =>
  state.app.peopleDetail.creditCardsFullPaginatedData

const selectUniqueCreditCardIds = createSelector(
  [selectCreditCardsFullPaginatedData],
  (data) => data.ids.filter((id, i) => data.ids.indexOf(id) === i),
)

const selectCreditCardOptions = createSelector(
  [selectUniqueCreditCardIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => ids.map((id) => creditCardOption(creditCards[id])),
)

export const selectFilteredCreditCardOptions = createSelector(
  [selectCreditCardOptions],
  (options) => options.filter(() => true),
)

export const selectusersOwnFilteredCreditCardOptions = createSelector(
  [selectFilteredCreditCardOptions, selectMemberIdFromRoute],
  (allCreditCards, memberId) =>
    allCreditCards.filter(
      (card) => card.details?.creditCard.ownerId === memberId,
    ),
)

export const selectFilteredCreditCardOptionsWithoutOwn = createSelector(
  [selectFilteredCreditCardOptions, selectMemberIdFromRoute],
  (allCreditCards, memberId) =>
    allCreditCards.filter(
      (card) => card.details?.creditCard.ownerId !== memberId,
    ),
)

export const selectDepartment = (
  state: RootState,
): UserDepartment | undefined => state.app.peopleDetail.department

export const selectBookkeeperSelectiveAccess = (
  state: RootState,
): BookkeeperAccountVisibilityTypes | undefined =>
  state.app.peopleDetail.accountVisibility?.type

const selectUserAccessAccountIds = (state: RootState): string[] =>
  state.app.peopleDetail.accountVisibility?.ids ?? []

export const selectUserCreditCardAccessOptions = createSelector(
  [selectUserAccessAccountIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => {
    if (creditCards !== undefined && Object.keys(creditCards).length !== 0) {
      return ids.map((id) => creditCardOption(creditCards[id]))
    }
  },
)

export const selectUserAccessCreditCards = createSelector(
  [selectUserAccessAccountIds, selectCreditCardListModelsFromState],
  (ids, creditCards) => {
    if (creditCards !== undefined && Object.keys(creditCards).length !== 0) {
      return ids.map((id) => creditCards[id])
    }
  },
)

export const selectUsersOwnAccounts = createSelector(
  [selectCreditCardListModelsFromState, selectMemberIdFromRoute],
  (creditCards, id) => {
    if (creditCards !== undefined && Object.keys(creditCards).length !== 0) {
      return Object.values(creditCards)
        .map((creditCard) => {
          if (creditCard.ownerId === id) {
            return creditCard
          }
        })
        .filter((id) => id !== undefined) as CreditCardListModel[]
    } else {
      return []
    }
  },
)
